import { day as formatDay, monthLabel } from '../format.js';

/**
 * Pure helpers for the charts explorer: the URL-held view state (so a chart is
 * bookmarkable), the date-range presets, and the label/axis formatting the
 * charts and their table fallback share. Nothing here imports the charting
 * library — these are plain functions, unit-tested in charts.test.ts.
 */

export const CHART_TYPES = [
  'sales_stock',
  'sales_time',
  'yoy',
  'top',
  'stockout_timeline',
  'sessions',
  'scatter',
  'sell_through',
  'size_mix',
] as const;
export type ChartType = (typeof CHART_TYPES)[number];

export const CHART_LABELS: Record<ChartType, string> = {
  sales_stock: 'Sales and stock',
  sales_time: 'Sales over time',
  yoy: 'This year vs last',
  top: 'Top sellers',
  stockout_timeline: 'Stock and stockout timeline',
  sessions: 'Sessions and conversion',
  scatter: 'Sell-through scatter',
  sell_through: 'Sell-through trend',
  size_mix: 'Size mix over time',
};

export const GRAINS = ['day', 'week', 'month'] as const;
export type Grain = (typeof GRAINS)[number];

/**
 * `asin` is one product (every seller SKU on the child ASIN), opened from the
 * product page; the scope picker offers it only when a view arrives with it.
 */
export const SCOPES = ['account', 'family', 'sku', 'asin'] as const;
export type Scope = (typeof SCOPES)[number];

export type RangePreset = '3m' | '6m' | '12m' | '24m' | 'custom';
export type Metric = 'units' | 'revenue';
export type TopBy = 'sku' | 'family';

/** The whole view, held in the URL so a chart can be bookmarked and shared. */
export interface ChartState {
  type: ChartType;
  scope: Scope;
  /** Family key, the numeric SKU id (as a string), an ASIN, or null for account scope. */
  key: string | null;
  grain: Grain;
  preset: RangePreset;
  /** Only meaningful when preset is 'custom'. */
  from: string;
  to: string;
  metric: Metric;
  by: TopBy;
  /** Overlay the previous year on the sales-over-time chart. */
  compare: boolean;
  /** Log scale on the scatter chart. */
  log: boolean;
  /** Show the underlying numbers as a table instead of the chart. */
  table: boolean;
}

const DEFAULTS: ChartState = {
  type: 'sales_stock',
  scope: 'account',
  key: null,
  grain: 'week',
  preset: '12m',
  from: '',
  to: '',
  metric: 'units',
  by: 'sku',
  compare: false,
  log: false,
  table: false,
};

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Reads the view state from URL search params, falling back to the defaults. */
export function decodeChartState(params: URLSearchParams): ChartState {
  const preset = oneOf(params.get('preset'), ['3m', '6m', '12m', '24m', 'custom'], DEFAULTS.preset);
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  return {
    type: oneOf(params.get('type'), CHART_TYPES, DEFAULTS.type),
    scope: oneOf(params.get('scope'), SCOPES, DEFAULTS.scope),
    key: params.get('key'),
    grain: oneOf(params.get('grain'), GRAINS, DEFAULTS.grain),
    preset,
    from: ISO_DAY.test(from) ? from : '',
    to: ISO_DAY.test(to) ? to : '',
    metric: oneOf(params.get('metric'), ['units', 'revenue'], DEFAULTS.metric),
    by: oneOf(params.get('by'), ['sku', 'family'], DEFAULTS.by),
    compare: params.get('compare') === '1',
    log: params.get('log') === '1',
    table: params.get('table') === '1',
  };
}

/** Writes only the values that differ from the defaults, for a clean URL. */
export function encodeChartState(state: ChartState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.type !== DEFAULTS.type) params.set('type', state.type);
  if (state.scope !== DEFAULTS.scope) params.set('scope', state.scope);
  if (state.key) params.set('key', state.key);
  if (state.grain !== DEFAULTS.grain) params.set('grain', state.grain);
  if (state.preset !== DEFAULTS.preset) params.set('preset', state.preset);
  if (state.preset === 'custom') {
    if (state.from) params.set('from', state.from);
    if (state.to) params.set('to', state.to);
  }
  if (state.metric !== DEFAULTS.metric) params.set('metric', state.metric);
  if (state.by !== DEFAULTS.by) params.set('by', state.by);
  if (state.compare) params.set('compare', '1');
  if (state.log) params.set('log', '1');
  if (state.table) params.set('table', '1');
  return params;
}

export const CHART_STATE_DEFAULTS = DEFAULTS;

// ---------------------------------------------------------------------------
// Date ranges
// ---------------------------------------------------------------------------

export const RANGE_PRESET_LABELS: Record<RangePreset, string> = {
  '3m': '3 months',
  '6m': '6 months',
  '12m': '12 months',
  '24m': '24 months',
  custom: 'Custom',
};

interface DateParts {
  y: number;
  m: number;
  d: number;
}

function fmtParts(p: DateParts): string {
  return `${String(p.y).padStart(4, '0')}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

/** Days in month m (1..12) of year y. */
function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Today's calendar date in a time zone (defaults to the runtime's own). */
function todayParts(today: Date, timeZone?: string): DateParts {
  if (timeZone) {
    const s = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(today);
    const [y, m, d] = s.split('-').map(Number);
    return { y: y!, m: m!, d: d! };
  }
  return { y: today.getUTCFullYear(), m: today.getUTCMonth() + 1, d: today.getUTCDate() };
}

/** N months before a date, clamping the day to the target month's end. */
function monthsBack(p: DateParts, months: number): DateParts {
  const total = p.y * 12 + (p.m - 1) - months;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return { y, m, d: Math.min(p.d, daysInMonth(y, m)) };
}

/**
 * The concrete {from, to} a state resolves to. A preset is N months back from
 * today (in the business time zone, if given) through today, with the start day
 * clamped to the month's end (May 31 minus 3 months is Feb 28/29). 'custom' uses
 * the state's own from/to (falling back to a 12-month window if either is missing).
 */
export function resolveRange(
  state: ChartState,
  opts: { today?: Date; timeZone?: string } = {},
): { from: string; to: string } {
  const cur = todayParts(opts.today ?? new Date(), opts.timeZone);
  const to = fmtParts(cur);
  if (state.preset === 'custom' && state.from && state.to) {
    return { from: state.from, to: state.to };
  }
  const months = state.preset === 'custom' ? 12 : Number(state.preset.replace('m', ''));
  return { from: fmtParts(monthsBack(cur, months)), to };
}

/** N days before a YYYY-MM-DD day. */
function shiftDays(iso: string, delta: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`);
  return new Date(t + delta * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The same window one year earlier, for the previous-year overlay. Months shift
 * by 12 calendar months (clamped to month end, so a leap Feb 29 becomes Feb 28);
 * days and weeks shift by 52 weeks (364 days) so week buckets stay aligned and
 * the two series line up bucket-for-bucket by index.
 */
export function previousYearRange(
  range: { from: string; to: string },
  grain: Grain,
): { from: string; to: string } {
  if (grain === 'month') {
    const shift = (iso: string) => {
      const [y, m, d] = iso.split('-').map(Number);
      return fmtParts(monthsBack({ y: y!, m: m!, d: d! }, 12));
    };
    return { from: shift(range.from), to: shift(range.to) };
  }
  return { from: shiftDays(range.from, -364), to: shiftDays(range.to, -364) };
}

// ---------------------------------------------------------------------------
// Labels and axes
// ---------------------------------------------------------------------------

/** A short axis label for a bucket start, given the grain. */
export function bucketLabel(start: string, grain: Grain): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(start);
  if (!m) return start;
  if (grain === 'month') return monthLabel(`${m[1]}-${m[2]}`);
  return `${m[2]}/${m[3]}`; // MM/DD for day and week (a week is labeled by its Sunday)
}

/**
 * The axis tick for a bucket, given the grain and whether the range spans more
 * than one calendar year. The chart keys its axis on the ISO bucket start (unique
 * per bucket, so Recharts never collapses repeated MM/DD labels to index
 * positions); this formats that ISO key for display, adding the year when needed.
 */
export function bucketTickLabel(startIso: string, grain: Grain, spansYears: boolean): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startIso);
  if (!m) return startIso;
  const [, y, mo, d] = m as unknown as [string, string, string, string];
  if (grain === 'month') return monthLabel(`${y}-${mo}`);
  return spansYears ? `${mo}/${d}/${y.slice(2)}` : `${mo}/${d}`;
}

/** True when a from..to range crosses a calendar-year boundary. */
export function rangeSpansYears(from: string, to: string): boolean {
  return from.slice(0, 4) !== to.slice(0, 4);
}

/** The full, exact date range of a bucket, for a tooltip or a table cell. */
export function bucketRangeLabel(start: string, end: string): string {
  return start === end ? formatDay(start) : `${formatDay(start)} – ${formatDay(end)}`;
}

/** A compact axis number: 1234 → "1.2k", 2500000 → "2.5M". */
export function axisNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${trimZero(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${trimZero(value / 1_000)}k`;
  return String(Math.round(value));
}

function trimZero(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '');
}

/**
 * The minimum pixel gap between x-axis tick labels, by grain and whether the
 * viewed range spans calendar years. Month labels ("Sep 2026") and year-tagged
 * day/week labels ("03/02/26") are wider than a bare "03/02", so they claim more
 * room; Recharts then drops any tick that would fall within this gap, so the
 * visible tick count adapts to the chart width on its own and labels never
 * overlap. Pure, so it is unit-tested across grain × range. Paired with
 * `bucketTickLabel`, these are the one date-formatting helper every series chart
 * shares for its x axis.
 */
export function axisTickGap(grain: Grain, spansYears: boolean): number {
  if (grain === 'month') return 44;
  return spansYears ? 40 : 26;
}

// ---------------------------------------------------------------------------
// Zoom and drag-to-select on the series charts (item a)
// ---------------------------------------------------------------------------

/** Below this many days a selection is fine enough for the daily grain. */
export const ZOOM_DAY_MAX = 60;
/** Up to this many days a selection reads well at the weekly grain; above it, monthly. */
export const ZOOM_WEEK_MAX = 730;

const GRAIN_RANK: Record<Grain, number> = { day: 0, week: 1, month: 2 };

export type ZoomMode = 'narrow' | 'refetch';

/**
 * A committed zoom: whether to narrow the already-loaded data client-side or
 * refetch the sub-range at a finer grain, the target grain, and the normalized
 * (from ≤ to) selected range.
 */
export interface ZoomView {
  mode: ZoomMode;
  grain: Grain;
  from: string;
  to: string;
}

/** The whole-day span between two YYYY-MM-DD days, inclusive. */
export function daySpan(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}

/** The finest grain that keeps a span readable (roughly 15–60 buckets). */
export function grainForSpan(days: number): Grain {
  if (days <= ZOOM_DAY_MAX) return 'day';
  if (days <= ZOOM_WEEK_MAX) return 'week';
  return 'month';
}

/**
 * Decides what a drag/zoom selection should do, given the grain the chart is
 * currently showing. If the selection is short enough that a finer grain fits,
 * the loaded grain is too coarse and the chart refetches the sub-range at that
 * finer grain (reusing the same bucketed-series endpoint, only with different
 * grain/from/to — never a second data path); otherwise the loaded data already
 * carries the detail and the chart merely narrows its plotted domain. A zoom
 * never coarsens the grain. Pure, so it is unit-tested across grain × range.
 */
export function planZoomSelection(sel: { from: string; to: string }, loadedGrain: Grain): ZoomView {
  const from = sel.from <= sel.to ? sel.from : sel.to;
  const to = sel.from <= sel.to ? sel.to : sel.from;
  const ideal = grainForSpan(daySpan(from, to));
  if (GRAIN_RANK[ideal] < GRAIN_RANK[loadedGrain]) {
    return { mode: 'refetch', grain: ideal, from, to };
  }
  return { mode: 'narrow', grain: loadedGrain, from, to };
}

/** The finer-grain ordering used to decide fetch-vs-filter (lower is finer). */
export function grainRank(grain: Grain): number {
  return GRAIN_RANK[grain];
}

/**
 * Resolves what the series chart should actually fetch, given the committed zoom
 * and the base (URL) range and grain. When the zoom's grain is finer than the
 * base grain it fetches the sub-range at the zoom grain (the finer detail the
 * base fetch lacks); otherwise it keeps the base fetch and reports `narrow` so
 * the caller slices the loaded buckets to the zoom range client-side. This is
 * what carries a refetched grain across a follow-up narrow zoom — the decision
 * rests on the grain, not on the zoom's mode label. Pure, so it is unit-tested.
 */
export function effectiveFetch(
  zoom: ZoomView | null,
  base: { from: string; to: string; grain: Grain },
): { from: string; to: string; grain: Grain; narrow: boolean } {
  if (!zoom) return { from: base.from, to: base.to, grain: base.grain, narrow: false };
  if (grainRank(zoom.grain) < grainRank(base.grain)) {
    return { from: zoom.from, to: zoom.to, grain: zoom.grain, narrow: false };
  }
  return { from: base.from, to: base.to, grain: base.grain, narrow: true };
}

/**
 * The covered date range of a drag selection, from the two selected bucket
 * starts. It clamps to each edge bucket's *covered* span (`coveredFrom` /
 * `coveredTo`), so a selection onto a clipped edge bucket never refetches days
 * outside the selection and never runs past the last known day into the future.
 * Order-independent. Pure, so it is unit-tested.
 */
export function selectionCoveredRange<
  B extends { start: string; coveredFrom: string; coveredTo: string },
>(buckets: readonly B[], fromKey: string, toKey: string): { from: string; to: string } {
  const lo = fromKey <= toKey ? fromKey : toKey;
  const hi = fromKey <= toKey ? toKey : fromKey;
  const first = buckets.find((b) => b.start === lo);
  const last = buckets.find((b) => b.start === hi);
  return { from: first ? first.coveredFrom : lo, to: last ? last.coveredTo : hi };
}

/** A committed zoom that also remembers the selected edge buckets' start keys. */
export interface CommittedZoom extends ZoomView {
  /**
   * The selected edge buckets' `start` keys, used only for the client-side narrow
   * filter. The covered dates in `from`/`to` drive the fetch and grain decision,
   * but filtering the loaded buckets by a covered date would drop a partial edge
   * bucket the user selected (its `start` precedes its `coveredFrom`), so the
   * filter compares bucket starts against these keys instead.
   */
  fromKey: string;
  toKey: string;
}

/**
 * Builds a committed zoom from a drag between two bucket starts: the covered
 * range (clamped to the edge buckets' covered spans) sets the fetch range and,
 * through `planZoomSelection`, the narrow-vs-refetch decision and target grain;
 * the bucket-start keys are carried through for the client-side narrow filter so
 * a selected partial edge bucket is kept. Pure, so it is unit-tested.
 */
export function planZoom<B extends { start: string; coveredFrom: string; coveredTo: string }>(
  buckets: readonly B[],
  leftKey: string,
  rightKey: string,
  loadedGrain: Grain,
): CommittedZoom {
  const fromKey = leftKey <= rightKey ? leftKey : rightKey;
  const toKey = leftKey <= rightKey ? rightKey : leftKey;
  const covered = selectionCoveredRange(buckets, fromKey, toKey);
  return { ...planZoomSelection(covered, loadedGrain), fromKey, toKey };
}

// ---------------------------------------------------------------------------
// Legend toggles (item c)
// ---------------------------------------------------------------------------

/**
 * Adds or removes a series' data key from the hidden set, returning a new array.
 * Kept per chart (never global) in the chart's own state; pure, so it is
 * unit-tested.
 */
export function toggleSeriesKey(hidden: readonly string[], key: string): string[] {
  return hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
}

// ---------------------------------------------------------------------------
// Top-sellers label readability (item d)
// ---------------------------------------------------------------------------

/**
 * Truncates a label to at most `maxChars`, ending in an ellipsis, for the
 * top-sellers category axis — the full name still shows in the tick's native
 * title and in the bar tooltip. Pure, so it is unit-tested.
 */
export function truncateLabel(text: string, maxChars: number): string {
  if (maxChars <= 0) return '';
  if (text.length <= maxChars) return text;
  if (maxChars === 1) return '…';
  return `${text.slice(0, maxChars - 1).trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// Sales-and-stock shading classifier
// ---------------------------------------------------------------------------

export type StockBandState = 'in' | 'stockout' | 'unknown';

export interface StockBand {
  state: StockBandState;
  /** daysStockout / known days (0..1); drives proportional stockout shading. */
  stockoutFraction: number;
}

/**
 * Classifies a bucket's stock state for the Sales-and-stock shading. A bucket
 * with no sku_day rows at all, or only unknown days, is `unknown` (hatched), not
 * left blank. A bucket with any stocked-out days carries a `stockoutFraction` so
 * a mostly-stocked-out month (say 29 of 30 days) is shaded even though it has one
 * in-stock day. Pure, so it is unit-tested directly.
 */
export function bucketStockBand(b: {
  daysInStock: number;
  daysStockout: number;
  daysUnknown: number;
  onHandTotalSkus: number;
}): StockBand {
  const known = b.daysInStock + b.daysStockout;
  // No sku_day presence, or every covered day unknown → unknown.
  if (b.onHandTotalSkus === 0 || known === 0) return { state: 'unknown', stockoutFraction: 0 };
  if (b.daysStockout === 0) return { state: 'in', stockoutFraction: 0 };
  if (b.daysInStock === 0) return { state: 'stockout', stockoutFraction: 1 };
  return { state: 'in', stockoutFraction: b.daysStockout / known };
}

/**
 * The x and width of bucket `index` of `count`, laid out uniformly across a plot
 * offset — the geometry the Sales-and-stock shading uses (bucket index → band
 * rectangle). Pure, so it is unit-tested against the axis band layout.
 */
export function bandRect(
  index: number,
  count: number,
  offset: { left: number; width: number },
): { x: number; width: number } {
  const width = count > 0 ? offset.width / count : 0;
  return { x: offset.left + index * width, width };
}

// ---------------------------------------------------------------------------
// Series → table rows
// ---------------------------------------------------------------------------

/** One bucket as a row for the table fallback: the raw bucket plus a range label. */
export interface SeriesRow<B> {
  bucket: B;
  rangeLabel: string;
}

/** Turns a series into table rows, each carrying its exact date-range label. */
export function seriesToRows<B extends { start: string; end: string }>(
  buckets: readonly B[],
): SeriesRow<B>[] {
  return buckets.map((bucket) => ({
    bucket,
    rangeLabel: bucketRangeLabel(bucket.start, bucket.end),
  }));
}
